import { useLayoutEffect, useMemo, useState, type FC } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { breakpoints, useMatchMedia, useWindowWidth } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, NakedButton, Text, Tray, Truncated } from '@frontend/design-system';
import { DemoPanelScopeProvider } from './demo-panel-context';
import { useDemoPanelShallowStore, type DemoPanelEntry, type DemoPanelHeaderConfig } from './demo-panel-store';

const DEFAULT_PANEL_WIDTH = 600;
const TRAY_MARGIN_PX = 80;

/**
 * Tracks the width of the element matching `selector`.
 * Returns `undefined` when no selector is provided or the element can't be found.
 */
const useMountTargetWidth = (selector: string | undefined, enabled: boolean) => {
  const [width, setWidth] = useState<number | undefined>(undefined);

  // Layout effect so the first measurement lands before paint (avoids a window-width -> container-width flash).
  useLayoutEffect(() => {
    const element = selector && enabled ? document.querySelector<HTMLElement>(selector) : null;
    if (!element) {
      setWidth(undefined);
      return;
    }

    // clientWidth (padding box) matches the box an absolutely positioned tray is laid out against.
    // Inside the observer callback layout is already computed, so reading it does not force a reflow.
    const update = () => {
      const next = Math.round(element.clientWidth);
      setWidth((prev) => (prev === next ? prev : next));
    };

    update();
    if (typeof ResizeObserver === 'undefined') return;

    // Observer fires for height changes too; `update` is a no-op when the width is unchanged.
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, [selector, enabled]);

  return width;
};

export interface DemoPanelDrawerProps {
  /**
   * CSS selector of the element the tray should mount into. When provided, the width of this element
   * (instead of the window width) is used to size and stack panels. The element should be `position: relative`.
   */
  mountTarget?: string;
}

export const DemoPanelDrawer: FC<DemoPanelDrawerProps> = ({ mountTarget }) => {
  const {
    panels,
    closeAll: handleClose,
    unregister,
    headerConfig,
  } = useDemoPanelShallowStore('panels', 'closeAll', 'unregister', 'headerConfig');

  const windowWidth = useWindowWidth();
  const mountTargetWidth = useMountTargetWidth(mountTarget, panels.length > 0);

  const hasContainer = mountTargetWidth !== undefined;
  const availableWidth = mountTargetWidth ?? windowWidth;

  const isXSmallWindow = useMatchMedia({ maxWidth: breakpoints.xsmall.max });
  const isXSmall = hasContainer ? availableWidth <= breakpoints.xsmall.max : isXSmallWindow;

  const fullWidth = hasContainer ? '100%' : '100vw';
  const totalPanelWidth = useMemo(() => panels.reduce((sum, p) => sum + (p.width ?? DEFAULT_PANEL_WIDTH), 0), [panels]);

  // Stack when on xsmall or when all panels don't fit side-by-side with some margin
  const isStacked = isXSmall || totalPanelWidth + TRAY_MARGIN_PX > availableWidth;

  const isOpen = panels.length > 0;
  const rootPanel = panels[0] as DemoPanelEntry | undefined;
  const topPanel = panels[panels.length - 1] as DemoPanelEntry | undefined;

  const displayPanels = useMemo(() => {
    // In flex-direction: row-reverse, index 0 is rendered at the far right.
    // Higher priority panels should render further to the right (lower index).
    // When priorities are equal, maintain the relative registration order.
    return [...panels].sort((a, b) => {
      const priorityA = a.priority ?? 0;
      const priorityB = b.priority ?? 0;
      if (priorityA !== priorityB) {
        return priorityB - priorityA;
      }
      return 0;
    });
  }, [panels]);

  const trayWidthPx = useMemo(() => {
    if (!isOpen) return 0;
    if (isXSmall) return fullWidth;
    if (isStacked) return Math.min(panels.at(-1)?.width ?? DEFAULT_PANEL_WIDTH, availableWidth);
    return Math.min(
      panels.reduce((sum, p) => sum + (p.width ?? DEFAULT_PANEL_WIDTH), 0),
      availableWidth
    );
  }, [panels, isOpen, isStacked, isXSmall, availableWidth, fullWidth]);

  if (!isOpen || !rootPanel || !topPanel) {
    return null;
  }

  return (
    <Tray
      show={isOpen}
      onClose={handleClose}
      width='large'
      mountTarget={mountTarget}
      zIndex={theme.zIndex.modals - 1}
      css={[
        styles.tray,
        css({
          width: trayWidthPx,
          maxWidth: isXSmall || isStacked ? fullWidth : hasContainer ? '95%' : '95vw',
        }),
      ]}
      showBackdrop
    >
      <DemoPanelHeader config={headerConfig} onClose={handleClose} />
      <div css={styles.body}>
        {displayPanels.map((panel, index) => {
          const stackIndex = panels.findIndex((p) => p.id === panel.id);
          const isTop = panel.id === topPanel.id && isStacked;
          const isVisible = !isStacked || isTop;
          const Component = panel.component;

          return (
            <div
              key={panel.id}
              data-testid={`demo-panel-${panel.id}`}
              css={[
                styles.panelSlot,
                !isStacked && index > 0 && styles.panelBorder,
                isStacked && styles.stackedSlot,
                !isVisible && styles.hidden,
              ]}
              style={{
                width: isStacked ? '100%' : panel.width ?? DEFAULT_PANEL_WIDTH,
                minWidth: isStacked ? undefined : panel.width ?? DEFAULT_PANEL_WIDTH,
                zIndex: isStacked ? stackIndex + 1 : undefined,
              }}
            >
              <DemoPanelScopeProvider panelId={panel.id} isTopOfStack={isTop} close={() => unregister(panel.id)}>
                <Component {...(panel.componentProps as Record<string, unknown>)} />
              </DemoPanelScopeProvider>
            </div>
          );
        })}
      </div>
    </Tray>
  );
};

const DemoPanelHeader: FC<{
  config?: DemoPanelHeaderConfig;
  onClose: () => void;
}> = ({ config, onClose }) => {
  const { t } = useTranslation('base');
  if (!config) return null;

  return (
    <div css={styles.headerBanner}>
      <div css={styles.headerTitle}>
        {config.icon || (config.iconName ? <Icon name={config.iconName} /> : null)}
        <div css={styles.headerText}>
          <Truncated as={Text} weight='bold'>
            {config.title ?? t('This is sample data.')}
          </Truncated>
          {config.description && (
            <Truncated as={Text} size='medium'>
              {config.description}
            </Truncated>
          )}
        </div>
      </div>
      <div css={styles.headerAction}>
        {config.action && 'render' in config.action ? (
          config.action.render
        ) : config.action ? (
          <Button variant='primary' onClick={config.action.onClick} trackingId={config.action.trackingId}>
            {config.action.title}
          </Button>
        ) : null}
      </div>
      <div css={styles.headerClose}>
        <NakedButton css={styles.closeBtn} trackingId='demo-panel-close-button' onClick={onClose}>
          <Icon name='x-small' />
        </NakedButton>
      </div>
    </div>
  );
};

const styles = {
  tray: css`
    padding: 0;
    gap: 0;
    transition: width 0.3s ease;
  `,
  body: css`
    display: flex;
    flex-direction: row-reverse;
    flex: 1;
    overflow: hidden;
    height: 100%;
    position: relative;
  `,
  panelSlot: css`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden auto;
    flex-shrink: 0;
  `,
  stackedSlot: css`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    background-color: ${theme.colors.white};
  `,
  panelBorder: css`
    border-right: 1px solid ${theme.colors.neutral20};
  `,
  hidden: css`
    display: none;
  `,
  headerBanner: css`
    display: grid;
    grid-template-areas: 'title action close';
    grid-template-columns: minmax(0, 1fr) auto auto;
    padding: ${theme.spacing(2)};
    gap: ${theme.spacing(2)};
    align-items: center;
    background-color: ${theme.colorsV2.seaweed50(10)};
    border-bottom: 1px solid ${theme.colors.secondary.seaweed50};
    transition: width 0.3s ease;
  `,
  headerTitle: css`
    grid-area: title;
    display: flex;
    min-width: 0;
    align-items: center;
    gap: ${theme.spacing(1)};
    > svg {
      flex-shrink: 0;
    }
  `,
  headerText: css`
    min-width: 0;
    flex: 1;
  `,
  headerAction: css`
    grid-area: action;
  `,
  headerClose: css`
    grid-area: close;
  `,
  closeBtn: css`
    border-radius: ${theme.borderRadius.full};
    background-color: ${theme.colors.white};
    padding: ${theme.spacing(1)};
  `,
};
